import React, { useState, useEffect, useCallback } from 'react';
import { X, Megaphone, Clock, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AnnouncementPopup() {
  const { getAuthHeader, user } = useAuth();
  const [announcement, setAnnouncement] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const checkUnseenAnnouncements = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/announcements/unseen/check`, getAuthHeader());
      if (response.data.has_unseen && response.data.announcement) {
        // Check if we've dismissed this in current session
        const dismissedKey = `announcement_dismissed_${response.data.announcement.id}`;
        const dismissedTime = sessionStorage.getItem(dismissedKey);
        
        if (dismissedTime) {
          // Check if popup duration has passed
          const dismissedDate = new Date(dismissedTime);
          const hoursElapsed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60);
          if (hoursElapsed < response.data.announcement.popup_duration_hours) {
            return; // Still within popup duration, don't show again
          }
        }
        
        setAnnouncement(response.data.announcement);
        setTimeout(() => setIsVisible(true), 500); // Small delay for smooth entrance
      }
    } catch (error) {
      console.error('Failed to check announcements:', error);
    }
  }, [user, getAuthHeader]);

  useEffect(() => {
    checkUnseenAnnouncements();
  }, [checkUnseenAnnouncements]);

  const handleDismiss = async () => {
    setIsClosing(true);
    
    // Mark as seen in backend
    if (announcement) {
      try {
        await axios.post(`${API_URL}/api/announcements/${announcement.id}/mark-seen`, {}, getAuthHeader());
      } catch (error) {
        console.error('Failed to mark announcement as seen:', error);
      }
      
      // Store dismissal time in session storage
      sessionStorage.setItem(`announcement_dismissed_${announcement.id}`, new Date().toISOString());
    }
    
    setTimeout(() => {
      setIsVisible(false);
      setAnnouncement(null);
      setIsClosing(false);
    }, 300);
  };

  const handleReadMore = () => {
    handleDismiss();
    // Navigate to announcements page
    window.location.href = '/duyurular';
  };

  if (!isVisible || !announcement) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleDismiss}
      />
      
      {/* Popup Content */}
      <div 
        className={`relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
      >
        {/* Close Button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-black/20 hover:bg-black/30 text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
        
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-lg">
              <Megaphone className="h-6 w-6" />
            </div>
            <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
              Yeni Duyuru
            </span>
          </div>
          <h2 className="text-2xl font-bold">{announcement.title}</h2>
        </div>
        
        {/* Image if exists */}
        {announcement.image_url && (
          <div className="relative h-48">
            <img 
              src={announcement.image_url} 
              alt={announcement.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </div>
        )}
        
        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {announcement.content}
          </p>
          
          {/* Meta info */}
          <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>
              {new Date(announcement.created_at).toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
        </div>
        
        {/* Actions */}
        <div className="px-6 pb-6 flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={handleDismiss}
          >
            Kapat
          </Button>
          <Button 
            className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            onClick={handleReadMore}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Tüm Duyurular
          </Button>
        </div>
      </div>
    </div>
  );
}
